/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { api, formatApiError, spriteUrl } from "@/lib/api";
import { useCountdown, ProgressBar, useNow } from "@/lib/time";
import { toast } from "sonner";
import { X, ArrowRightLeft, Star, Sparkles, Map, Lock, ArrowDown, ArrowUp, Phone } from "lucide-react";
import PokemonDetailModal from "@/components/PokemonDetailModal";
import RegionMap from "@/components/RegionMap";
import ForceActionModal from "@/components/ForceActionModal";

const DIRECTIVE_LABELS = {
  free: "Fais ce qu'il te plait",
  gym_ready: "Tu es prêt pour l'arène",
  prio_unknown: "Capture en priorité les inconnus",
  high_level_capture: "Capture à haut niveau",
  high_level_train: "Entraîne-toi à haut niveau",
  explore_priority: "Explore en priorité",
};

const DIRECTIVE_DESCRIPTIONS = {
  free: "Comportement par défaut selon sa personnalité (sans tenter l'arène).",
  gym_ready: "Autorise les tentatives d'arène. Se réinitialise après une victoire.",
  prio_unknown: "Cible les routes contenant des Pokémon non répertoriés.",
  high_level_capture: "Privilégie les zones les plus avancées accessibles pour capturer.",
  high_level_train: "Privilégie les zones les plus dures pour gagner plus d'XP.",
  explore_priority: "Rehausse fortement le taux d'exploration.",
};

const PERSONALITY_LABELS = {
  battler: "Combattant", explorer: "Explorateur",
  collector: "Collectionneur", balanced: "Équilibré",
};

const KIND_LABELS = {
  starter: "Starter", wild: "Sauvage", legendary: "Légendaire", trade: "Échange",
};

function shinySprite(pid, isShiny) {
  return isShiny
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${pid}.png`
    : spriteUrl(pid);
}

export default function TrainerDetailModal({ trainerId, open, onClose, regions = [], initialTab = "team" }) {
  const [data, setData] = useState(null);
  const [heritage, setHeritage] = useState(null);
  const [forceAction, setForceAction] = useState(null); // 'explore' | 'capture' | 'train'
  const [profInventory, setProfInventory] = useState([]);
  const [tab, setTab] = useState("team");
  const [busy, setBusy] = useState(false);
  const [pokeDetail, setPokeDetail] = useState(null); // {poke, context}
  const [regionSwitch, setRegionSwitch] = useState(null); // {region, preview, chosenStarter}
  const now = useNow(1000);

  const load = async () => {
    if (!trainerId) return;
    try {
      const { data } = await api.get(`/trainers/${trainerId}`);
      setData(data);
    }
    catch (e) { console.error(e); }
  };

  const loadProfInventory = async () => {
    try {
      const { data } = await api.get("/inventory");
      setProfInventory(data);
    } catch (e) { console.error(e); }
  };

  const loadHeritage = async () => {
    if (!trainerId) return;
    try {
      const { data } = await api.get(`/trainers/${trainerId}/heritage`);
      setHeritage(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!open) return;
    setTab(initialTab || "team");
    setHeritage(null);
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trainerId, initialTab]);

  useEffect(() => {
    if (tab === "heritage" && open) loadHeritage();
    if (tab === "bag" && open) loadProfInventory();
  }, [tab, open]);

  if (!open || !data) return null;

  const t = data.trainer;
  const { label: timer, urgent } = makeCountdown(t.action_ends_at);
  const isMaster = t.league_status === "completed";
  const totalCaught = (t.caught_history || []).length;
  const shinyCount = (t.caught_history || []).filter(c => c.is_shiny).length;

  const removeFromTeam = async (slot) => {
    if (t.team.length <= 1) { toast.error("Le dresseur doit garder au moins 1 Pokémon."); return; }
    setBusy(true);
    try {
      await api.post(`/trainers/${trainerId}/team/remove/${slot}`);
      await load();
      toast.success("Pokémon envoyé au PC du labo.");
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const transferToTeam = async (pcId) => {
    if (t.team.length >= 6) { toast.error("Équipe pleine (6/6)"); return; }
    setBusy(true);
    try { await api.post(`/pc/${pcId}/transfer/${trainerId}`); await load(); toast.success("Pokémon ajouté à l'équipe."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openRegionSwitch = async (region) => {
    try {
      const { data } = await api.get(`/trainers/${trainerId}/region-preview/${region.id}`);
      setRegionSwitch({ region, preview: data, chosenStarter: null });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmRegionSwitch = async () => {
    if (!regionSwitch) return;
    const { region, preview, chosenStarter } = regionSwitch;
    if (preview.needs_starter && !chosenStarter) {
      toast.error("Choisis un starter avant de valider.");
      return;
    }
    setBusy(true);
    try {
      const payload = { region_id: region.id };
      if (preview.needs_starter && chosenStarter) payload.starter_pokemon_id = chosenStarter;
      const { data } = await api.post(`/trainers/${trainerId}/region`, payload);
      await load();
      if (data.restored) {
        toast.success(`Retour à ${region.name} — équipe récupérée (${data.restored_team_size}).`);
      } else {
        toast.success(`Départ pour ${region.name} !`);
      }
      setRegionSwitch(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const moveRegion = async (regionId) => {
    // Kept as a fallback (used elsewhere in the codebase); UI now uses openRegionSwitch.
    if (!window.confirm(`Envoyer ${t.name} en région ${regionId} ? Il ne pourra revenir qu'après avoir conquis sa Ligue.`)) return;
    try { await api.post(`/trainers/${trainerId}/region`, { region_id: regionId }); await load(); toast.success("Région assignée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setPhone = async (directive) => {
    try {
      await api.post(`/trainers/${trainerId}/phone`, { directive });
      await load();
      const label = DIRECTIVE_LABELS[directive] || directive;
      toast.success(`📞 « ${label} » transmis à ${t.name}.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const giveItem = async (item_id, qty) => {
    if (!qty || qty < 1) return;
    setBusy(true);
    try { await api.post(`/trainers/${trainerId}/give`, { item_id, qty }); await load(); await loadProfInventory(); toast.success("Objet remis au dresseur."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const takeItem = async (item_id, qty) => {
    if (!qty || qty < 1) return;
    setBusy(true);
    try { await api.post(`/trainers/${trainerId}/take`, { item_id, qty }); await load(); await loadProfInventory(); toast.success("Objet récupéré au labo."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const consumeItem = async (item_id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/trainers/${trainerId}/use-item`, { item_id });
      await load();
      toast.success(res?.message || "Objet utilisé.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Trigger a strategy override from within the modal. Explore/capture/train
  // need a route selection → open ForceActionModal. Rest/gym/legendary are
  // one-shot POST /strategy calls with no target.
  const runStrategy = async (strategy) => {
    if (["explore", "capture", "train"].includes(strategy)) {
      setForceAction(strategy);
      return;
    }
    try {
      await api.post(`/trainers/${trainerId}/strategy`, { strategy });
      await load();
      if (strategy === "rest") toast.success("Repos forcé — placé en prochaine action.");
      if (strategy === "gym") toast.success("Tentative d'arène forcée.");
      if (strategy === "legendary") toast.success("Chasse au légendaire lancée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 overflow-auto"
      style={{ background: "rgba(26,26,26,0.55)", zIndex: 70 }}
      onClick={onClose}
      data-testid="trainer-detail-modal"
    >
      <div className="panel-strong p-0 max-w-4xl w-full my-8 relative" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="detail-close-btn"
          aria-label="Fermer la fiche du dresseur"
          className="absolute top-2 right-2 z-20 w-11 h-11 rounded-full bg-[#1A1A1A] text-[#F4EFE6] border-2 border-[#F4EFE6] flex items-center justify-center shadow-lg hover:bg-[#8C2A2A] transition-colors"
          onClick={onClose}
        >
          <X size={22} strokeWidth={2.5}/>
        </button>

        {/* Header */}
        <header className="p-5 border-b border-[#2B2B2B] bg-[#EAE4D9]">
          <div className="flex items-start gap-4">
            <img
              src={`https://play.pokemonshowdown.com/sprites/trainers/${t.sprite_id || "youngster"}.png`}
              alt={t.name}
              className="pixelated w-32 h-32 md:w-40 md:h-40 shrink-0"
              style={{ imageRendering: "pixelated", filter: "drop-shadow(3px 3px 0 rgba(30,45,36,0.35))" }}
              data-testid="trainer-detail-avatar"
              onError={(e) => { e.currentTarget.src = "https://play.pokemonshowdown.com/sprites/trainers/youngster.png"; }}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="overline">{PERSONALITY_LABELS[t.personality]} · Niv {t.level}</div>
                {isMaster && <span className="stamp text-[9px]" style={{transform:"rotate(-2deg)"}}>MAÎTRE</span>}
                {t.has_shiny_charm && <span className="font-mono text-[10px] border border-[#B87333] bg-[#FCF9F2] text-[#B87333] px-1.5 py-0.5">💎 Charme</span>}
                {t.recent_defeats > 0 && <span className="font-mono text-[10px] border border-[#8C2A2A] text-[#8C2A2A] px-1.5 py-0.5">défaite récente</span>}
                {t.forced_next_action && <span className="font-mono text-[10px] border border-[#1A1A1A] bg-[#1A1A1A] text-[#F4EFE6] px-1.5 py-0.5">forçage : {t.forced_next_action}</span>}
                {t.travel_destination && (t.travel_queue?.length || 0) > 0 && (
                  <span className="font-mono text-[10px] border border-[#7A5A18] bg-[#F6C74722] text-[#7A5A18] px-1.5 py-0.5"
                    data-testid="detail-travel-badge">
                    🗺️ Déplacement · {t.travel_queue.length} étape(s)
                  </span>
                )}
                {t.phone_directive && t.phone_directive !== "free" && (
                  <span className="font-mono text-[10px] text-[#8C2A2A] border border-[#8C2A2A] bg-[#FAEDED] px-1.5 py-0.5">
                    📞 {DIRECTIVE_LABELS[t.phone_directive]}
                  </span>
                )}
                {(() => {
                  const boostUntil = t.speed_boost_until;
                  const mult = t.speed_boost_mult;
                  if (!boostUntil || !mult) return null;
                  const remainMs = new Date(boostUntil).getTime() - now;
                  if (remainMs <= 0) return null;
                  const remainMin = Math.ceil(remainMs / 60000);
                  return (
                    <span
                      className="font-mono text-[10px] border border-[#B87333] bg-[#FCF9F2] text-[#B87333] px-1.5 py-0.5 inline-flex items-center gap-1"
                      data-testid="detail-boost-badge"
                    >
                      ⚡ Vitesse ×{mult} · {remainMin} min
                    </span>
                  );
                })()}
              </div>
              <h2 className="font-serif-display text-3xl leading-none mt-1" data-testid="detail-name">{t.name}</h2>
              <div className="mt-2 text-xs font-mono text-[#4A4A4A]">
                Starter : {t.starter_name}{t.starter_shiny ? " ✨" : ""} ·
                {" "}Région : {(t.current_region || "kanto").toUpperCase()} ·
                {" "}{t.badges?.length || 0}/8 badges
              </div>
              {/* Stamina bar */}
              {(() => {
                const stamina = t.stamina ?? 100;
                const staminaColor = stamina >= 60 ? "#2E4F32" : stamina >= 30 ? "#B87333" : "#8C2A2A";
                return (
                  <div className="mt-2" data-testid="detail-stamina">
                    <div className="flex justify-between text-[10px] font-mono text-[#4A4A4A]">
                      <span>Stamina</span>
                      <span style={{color: staminaColor}}>{stamina}/100</span>
                    </div>
                    <div className="h-[5px] bg-[#FCF9F2] border border-[#2B2B2B] mt-0.5">
                      <div className="h-full transition-all" style={{width: `${stamina}%`, backgroundColor: staminaColor}}/>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="text-right">
              <div className="overline">Action</div>
              <div className="font-mono text-sm">{t.current_action.toUpperCase()}</div>
              <div className={`font-mono text-xs ${urgent ? "timer-urgent" : "text-[#4A4A4A]"}`} data-testid="detail-timer">{timer}</div>
              <ProgressBar startIso={t.action_started_at} endIso={t.action_ends_at}/>
            </div>
          </div>

          {/* Advice panel */}
          {data.advice && data.advice.length > 0 && (
            <div className="mt-4 panel p-3 bg-[#FCF9F2]" data-testid="advice-panel">
              <div className="overline mb-2">📜 Conseils du Professeur</div>
              <ul className="space-y-1">
                {data.advice.map((a, i) => (
                  <li key={i} className="text-sm flex items-center gap-2" data-testid={`advice-${i}`}>
                    <span className={`font-mono text-[9px] uppercase px-1 border ${
                      a.priority === "high" ? "border-[#8C2A2A] text-[#8C2A2A]"
                      : a.priority === "med" ? "border-[#B87333] text-[#B87333]"
                      : "border-[#7A7A7A] text-[#7A7A7A]"
                    }`}>
                      {a.priority}
                    </span>
                    <span>{a.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Counters */}
          <div className="grid grid-cols-5 gap-2 mt-4">
            <Mini label="Captures" value={t.capture_count || 0}/>
            <Mini label="Explorations" value={t.explore_count || 0}/>
            <Mini label="Total capturés" value={totalCaught} data-testid="detail-total-caught"/>
            <Mini label="Shinies" value={shinyCount} accent={shinyCount > 0}/>
            <Mini
              label="Poké Balls"
              value={((data.inventory || []).find(i => i.item_id === "pokeball")?.qty) || 0}
              accent={(((data.inventory || []).find(i => i.item_id === "pokeball")?.qty) || 0) === 0}
              data-testid="detail-pokeballs"
            />
          </div>
        </header>

        {/* Force-action bar — quickly launch a specific action without
            leaving the popup. Explore/Capture/Train open the route picker;
            Rest/Arène/Légendaire are one-shot. */}
        <div className="px-5 py-2 border-b border-[#2B2B2B] bg-[#F5EAD1] flex flex-wrap items-center gap-1.5">
          <span className="overline text-[9px] mr-1">Forcer une action</span>
          <button
            data-testid="detail-force-explore"
            className="btn-ink text-[10px] py-1 px-2"
            title="Imposer une exploration sur une route limitrophe"
            onClick={() => runStrategy("explore")}
          >Explorer</button>
          <button
            data-testid="detail-force-capture"
            className="btn-ink text-[10px] py-1 px-2"
            title="Imposer une capture sur une route limitrophe"
            onClick={() => runStrategy("capture")}
          >Capturer</button>
          <button
            data-testid="detail-force-train"
            className="btn-ink text-[10px] py-1 px-2"
            title="Imposer un entraînement sur une route limitrophe"
            onClick={() => runStrategy("train")}
          >Entraîner</button>
          <button
            data-testid="detail-force-gym"
            className="btn-ink text-[10px] py-1 px-2"
            title="Tenter l'arène de la ville actuelle"
            onClick={() => runStrategy("gym")}
          >Arène</button>
          <button
            data-testid="detail-force-rest"
            className="btn-ink text-[10px] py-1 px-2"
            title="Forcer un repos (placé en prochaine action)"
            onClick={() => runStrategy("rest")}
          >Repos</button>
          {(t.badges?.length || 0) >= 8 && (
            <button
              data-testid="detail-force-legendary"
              className="btn-ink text-[10px] py-1 px-2 text-[#B87333] border-[#B87333]"
              title="Chasser un légendaire (nécessite 8 badges)"
              onClick={() => runStrategy("legendary")}
            >🌟 Légendaire</button>
          )}
        </div>

        {/* Tabs */}
        <nav className="flex border-b border-[#2B2B2B] overflow-x-auto">
          {[
            { id: "team", label: `Équipe (${t.team?.length || 0}/6)` },
            { id: "phone", label: "Téléphone" },
            { id: "pc", label: `PC perso (${data.pc_pokemon?.length || 0})` },
            { id: "bag", label: `Sac (${(data.inventory || []).reduce((s,i)=>s+i.qty,0)})` },
            { id: "history", label: `Historique (${totalCaught})` },
            { id: "heritage", label: "Héritage" },
            { id: "map", label: "Carte" },
            { id: "region", label: "Région" },
          ].map(x => (
            <button
              key={x.id}
              data-testid={`detail-tab-${x.id}`}
              onClick={() => setTab(x.id)}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-wider border-r border-[#2B2B2B] ${
                tab === x.id ? "bg-[#1A1A1A] text-[#F4EFE6]" : "text-[#1A1A1A] hover:bg-[#EAE4D9]"
              }`}
            >
              {x.label}
            </button>
          ))}
        </nav>

        <div className="p-5 max-h-[60vh] overflow-auto">
          {tab === "team" && (
            <div data-testid="detail-team-grid">
              <p className="text-[11px] text-[#4A4A4A] font-mono mb-3">
                L&apos;équipe combat dans les arènes. Composez-la pour exploiter les faiblesses du chef d&apos;arène.
              </p>

              {/* Next gym preview */}
              {data.gym_preview && (
                <div className="panel p-3 mb-3 bg-[#FCF9F2]" data-testid="gym-preview">
                  <div className="flex justify-between items-baseline mb-1">
                    <div className="overline">⚔️ Prochaine arène · {data.gym_preview.gym.name}</div>
                    <div className="font-mono text-xs">
                      Chef : <strong>{data.gym_preview.gym.leader}</strong> · {data.gym_preview.gym.types.join(" / ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <div>
                      <div className="overline text-[9px]">Victoire estimée</div>
                      <div
                        className="font-serif-display text-4xl leading-none"
                        style={{ color: data.gym_preview.win_chance >= 0.6 ? "#2E4F32" : data.gym_preview.win_chance >= 0.35 ? "#B87333" : "#8C2A2A" }}
                        data-testid="gym-preview-winchance"
                      >
                        {Math.round(data.gym_preview.win_chance * 100)}%
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="h-2 bg-[#EAE4D9] border border-[#2B2B2B]">
                        <div
                          className="h-full"
                          style={{
                            width: `${data.gym_preview.win_chance * 100}%`,
                            background: data.gym_preview.win_chance >= 0.6 ? "#2E4F32" : data.gym_preview.win_chance >= 0.35 ? "#B87333" : "#8C2A2A",
                          }}
                        />
                      </div>
                      {data.gym_preview.team_breakdown && data.gym_preview.team_breakdown.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1 text-[10px] font-mono">
                          {data.gym_preview.team_breakdown.map((b, i) => (
                            <div key={i} className="flex justify-between border border-[#EAE4D9] px-1 py-0.5">
                              <span className="truncate">{b.name} <span className="text-[#7A7A7A]">Lv{b.level}</span></span>
                              <span>
                                <span className={b.offensive > 0 ? "text-[#2E4F32]" : b.offensive < 0 ? "text-[#8C2A2A]" : "text-[#7A7A7A]"}>
                                  {b.offensive > 0 ? "+" : ""}{b.offensive} off
                                </span>
                                {" "}/
                                <span className={b.defensive > 0 ? "text-[#2E4F32]" : b.defensive < 0 ? "text-[#8C2A2A]" : "text-[#7A7A7A]"}>
                                  {" "}{b.defensive > 0 ? "+" : ""}{b.defensive} def
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {t.team?.map((p, i) => (
                  <div
                    key={i}
                    className="panel p-2 text-center flex flex-col items-center cursor-pointer hover:bg-[#FCF9F2]"
                    data-testid={`team-slot-${i}`}
                    onClick={(e) => {
                      if (e.target.closest("button")) return;
                      setPokeDetail({ poke: { ...p, caught_by_trainer_name: t.name }, context: "team" });
                    }}
                  >
                    <img src={shinySprite(p.pokemon_id, p.is_shiny)} alt={p.name} className="pixelated w-16 h-16"/>
                    <div className="font-serif-display text-base leading-none mt-1">
                      {p.name}{p.is_shiny ? " ✨" : ""}
                      {p.gender === "M" && <span className="text-[#2E5EAA] ml-1">♂</span>}
                      {p.gender === "F" && <span className="text-[#B8336A] ml-1">♀</span>}
                    </div>
                    <div className="font-mono text-[10px] text-[#4A4A4A]">Lv {p.level}</div>
                    <div
                      className={`font-mono text-[9px] mt-0.5 ${(p.iv ?? 91) >= 140 ? "text-[#B87333]" : (p.iv ?? 91) >= 100 ? "text-[#2E4F32]" : "text-[#7A7A7A]"}`}
                      title={p.iv_stats ? `IV total ${p.iv}/186 (réf 91)\nHP ${p.iv_stats[0]} · Att ${p.iv_stats[1]} · Déf ${p.iv_stats[2]}\nAtt.Sp ${p.iv_stats[3]} · Déf.Sp ${p.iv_stats[4]} · Vit ${p.iv_stats[5]}` : "IV total (91 = défaut)"}
                    >
                      IV {p.iv ?? 91}{(p.iv ?? 91) >= 140 ? " ★" : ""}
                    </div>
                    <div className="w-full h-[3px] bg-[#EAE4D9] border border-[#2B2B2B] mt-1">
                      <div className="h-full bg-[#2E4F32]" style={{width: `${Math.min(100, ((p.xp || 0) / (p.level * 30)) * 100)}%`}}/>
                    </div>
                    <button
                      className="btn-ink btn-accent text-[10px] py-0.5 px-1 mt-2 w-full justify-center"
                      onClick={(e) => { e.stopPropagation(); removeFromTeam(i); }}
                      disabled={busy || t.team.length <= 1}
                      data-testid={`team-remove-${i}`}
                    >
                      <ArrowRightLeft size={9}/> Au PC
                    </button>
                  </div>
                ))}
              </div>

              {/* Legendaries */}
              {(t.legendaries_caught || []).length > 0 && (
                <>
                  <div className="overline mt-5 mb-2">Légendaires capturés ({t.legendaries_caught.length}/3)</div>
                  <div className="flex gap-2 flex-wrap">
                    {t.legendaries_caught.map((pid) => (
                      <div key={pid} className="panel p-2 text-center" data-testid={`legendary-${pid}`}>
                        <img src={spriteUrl(pid)} alt="" className="pixelated w-12 h-12"/>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "pc" && (
            <div data-testid="detail-pc-grid">
              <p className="text-[11px] text-[#4A4A4A] font-mono mb-3">
                Pokémon que ce dresseur a capturés et qui sont actuellement au PC.
                Transférez-les dans son équipe pour préparer une arène.
              </p>
              {data.pc_pokemon.length === 0 ? (
                <div className="text-xs text-[#7A7A7A] font-mono">Aucun Pokémon au PC pour ce dresseur.</div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {data.pc_pokemon.map((p) => {
                    const lockedOut = p.region_locked && p.region_locked !== (t.current_region || "kanto");
                    return (
                    <div
                      key={p.id}
                      className="panel p-2 text-center cursor-pointer hover:bg-[#FCF9F2] relative"
                      data-testid={`pc-personal-${p.id}`}
                      onClick={(e) => {
                        if (e.target.closest("button")) return;
                        setPokeDetail({ poke: { ...p, caught_by_trainer_name: t.name }, context: "pc" });
                      }}
                    >
                      {p.region_locked && (
                        <span
                          className="absolute top-1 left-1 stamp text-[7px] px-1 py-[1px] rotate-[-4deg]"
                          title={`Verrouillé sur ${p.region_locked.toUpperCase()} — ce Pokémon ne peut rejoindre un dresseur qu'en ${p.region_locked}.`}
                          data-testid={`pc-lock-${p.id}`}
                        >
                          <Lock size={8} className="inline -mt-0.5 mr-0.5"/>
                          {p.region_locked.toUpperCase()}
                        </span>
                      )}
                      <img src={shinySprite(p.pokemon_id, p.is_shiny)} alt={p.name} className="pixelated w-14 h-14"/>
                      <div className="font-serif-display text-sm leading-none mt-1">
                        {p.name}{p.is_shiny ? " ✨" : ""}
                        {p.gender === "M" && <span className="text-[#2E5EAA] ml-1">♂</span>}
                        {p.gender === "F" && <span className="text-[#B8336A] ml-1">♀</span>}
                      </div>
                      <div className="font-mono text-[10px] text-[#4A4A4A]">Lv {p.level}</div>
                      {/* XP mini-bar */}
                      <div className="w-full h-[3px] bg-[#EAE4D9] border border-[#2B2B2B] mt-1" title={`XP ${p.xp || 0}/${p.level * 30}`}>
                        <div className="h-full bg-[#2E4F32]" style={{ width: `${Math.min(100, ((p.xp || 0) / (p.level * 30)) * 100)}%` }}/>
                      </div>
                      <div
                        className={`font-mono text-[9px] mt-1 ${(p.iv ?? 91) >= 140 ? "text-[#B87333]" : (p.iv ?? 91) >= 100 ? "text-[#2E4F32]" : "text-[#7A7A7A]"}`}
                        title={p.iv_stats ? `IV total ${p.iv}/186\nHP ${p.iv_stats[0]} · Att ${p.iv_stats[1]} · Déf ${p.iv_stats[2]}\nAtt.Sp ${p.iv_stats[3]} · Déf.Sp ${p.iv_stats[4]} · Vit ${p.iv_stats[5]}` : "IV total"}
                      >
                        IV {p.iv ?? 91}{(p.iv ?? 91) >= 140 ? " ★" : ""}
                      </div>
                      <button
                        className="btn-ink btn-leaf text-[10px] py-0.5 px-1 mt-2 w-full justify-center"
                        onClick={(e) => { e.stopPropagation(); transferToTeam(p.id); }}
                        disabled={busy || t.team.length >= 6 || lockedOut}
                        title={lockedOut ? `Verrouillé sur ${p.region_locked.toUpperCase()}` : ""}
                        data-testid={`pc-transfer-${p.id}`}
                      >
                        <ArrowRightLeft size={9}/> Équipe
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "phone" && (
            <div data-testid="detail-phone">
              <div className="flex items-start gap-3 mb-4">
                <Phone size={22} className="text-[#2E4F32] shrink-0 mt-1"/>
                <div>
                  <h3 className="font-serif-display text-xl leading-tight">Téléphone du Professeur</h3>
                  <p className="font-mono text-[11px] text-[#4A4A4A] mt-1">
                    {t.name} demande : « Que dois-je faire ? »<br/>
                    Donne-lui une consigne. Tant qu&apos;aucune autorisation explicite n&apos;est donnée, il ne tente plus les arènes.
                  </p>
                </div>
              </div>

              <div className="overline mb-2">
                Directive active : <strong>{DIRECTIVE_LABELS[t.phone_directive] || "Fais ce qu'il te plait"}</strong>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.keys(DIRECTIVE_LABELS).map((key) => {
                  const isActive = (t.phone_directive || "free") === key;
                  return (
                    <button
                      key={key}
                      data-testid={`phone-${key}`}
                      onClick={() => setPhone(key)}
                      className={`panel p-3 text-left ${isActive ? "border-[#B87333] bg-[#FCF9F2]" : "hover:bg-[#FCF9F2]"}`}
                    >
                      <div className="font-serif-display text-base leading-tight">
                        {DIRECTIVE_LABELS[key]}
                        {isActive && <span className="ml-2 font-mono text-[10px] text-[#B87333]">● actif</span>}
                      </div>
                      <div className="font-mono text-[10px] text-[#4A4A4A] mt-1 leading-snug">
                        {DIRECTIVE_DESCRIPTIONS[key]}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "bag" && (
            <div data-testid="detail-bag">
              <p className="text-[11px] text-[#4A4A4A] font-mono mb-3">
                Sac personnel du dresseur. Les captures consomment ses Poké Balls.
                Le Professeur peut lui donner des objets du labo ou récupérer ceux qu&apos;il a trouvés en exploration.
              </p>

              {/* Trainer-owned items */}
              <div className="overline mb-2">Possède actuellement</div>
              {(data.inventory || []).length === 0 ? (
                <div className="text-xs text-[#7A7A7A] font-mono mb-4">Sac vide. Donnez-lui au moins des Poké Balls pour qu&apos;il puisse capturer.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
                  {data.inventory.map((it) => (
                    <ItemRow
                      key={`t-${it.item_id}`}
                      item={it}
                      side="trainer"
                      max={it.qty}
                      onTransfer={(qty) => takeItem(it.item_id, qty)}
                      onUse={it.type === "consumable" ? () => consumeItem(it.item_id) : null}
                      busy={busy}
                      testid={`trainer-item-${it.item_id}`}
                    />
                  ))}
                </div>
              )}

              {/* Professor's lab inventory → give */}
              <div className="overline mb-2 mt-2">Donner depuis le labo</div>
              {profInventory.length === 0 ? (
                <div className="text-xs text-[#7A7A7A] font-mono">Aucun objet en stock au labo.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {profInventory.map((it) => (
                    <ItemRow
                      key={`p-${it.item_id}`}
                      item={it}
                      side="lab"
                      max={it.qty}
                      onTransfer={(qty) => giveItem(it.item_id, qty)}
                      busy={busy}
                      testid={`lab-item-${it.item_id}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "history" && (
            <div data-testid="detail-history">
              {totalCaught === 0 ? (
                <div className="text-xs text-[#7A7A7A] font-mono">Aucune capture pour l&apos;instant.</div>
              ) : (
                <ul className="ruled">
                  {[...(t.caught_history || [])].reverse().map((c, i) => (
                    <li key={i} className={`flex items-center gap-3 px-2 py-1 ${i % 2 ? "bg-[#FCF9F2]" : ""}`}>
                      <img src={shinySprite(c.pokemon_id, c.is_shiny)} alt="" className="pixelated w-8 h-8"/>
                      <div className="flex-1">
                        <div className="text-sm">
                          <strong>{c.name}</strong>
                          {c.is_shiny && <Sparkles size={12} className="inline ml-1 text-[#B87333]"/>}
                          <span className="text-[#4A4A4A] text-[11px] ml-2">Lv {c.level_at_catch} · {KIND_LABELS[c.kind] || c.kind}</span>
                        </div>
                        <div className="font-mono text-[10px] text-[#7A7A7A]">{new Date(c.caught_at).toLocaleString()}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "heritage" && (
            <div data-testid="detail-heritage">
              {!heritage ? (
                <div className="font-mono text-xs text-[#7A7A7A]">Chargement de l&apos;héritage…</div>
              ) : (
                <div>
                  <div className="text-center mb-5 pb-3 border-b border-[#2B2B2B]">
                    <div className="overline">Carnet du Professeur · Notes manuscrites</div>
                    <h3 className="font-serif-display italic text-3xl leading-tight mt-1">
                      Le parcours de {t.name}
                    </h3>
                    <p className="text-[11px] font-mono text-[#4A4A4A] mt-1">
                      Recruté(e) le {new Date(heritage.recruited_at).toLocaleDateString()} ·
                      {" "}{(t.current_region || "kanto").toUpperCase()}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <HeritageCard
                      icon="🌱"
                      title="Premier Pokémon"
                      sprite={heritage.starter ? shinySprite(heritage.starter.pokemon_id, heritage.starter.is_shiny) : null}
                      name={heritage.starter?.name}
                      detail={heritage.starter ? `Reçu le ${new Date(heritage.recruited_at).toLocaleDateString()}${heritage.starter.is_shiny ? " ✨ chromatique !" : ""}` : "—"}
                      testid="heritage-starter"
                    />
                    <HeritageCard
                      icon="🏅"
                      title="Premier badge"
                      name={heritage.first_badge ? `${heritage.first_badge.gym_name}` : null}
                      detail={heritage.first_badge ? `Vaincu(e) ${heritage.first_badge.leader} le ${new Date(heritage.first_badge.at).toLocaleDateString()}` : "Pas encore de badge."}
                      testid="heritage-first-badge"
                    />
                    <HeritageCard
                      icon="✨"
                      title="Premier shiny"
                      sprite={heritage.first_shiny ? shinySprite(heritage.first_shiny.pokemon_id, true) : null}
                      name={heritage.first_shiny?.name}
                      detail={heritage.first_shiny ? `Capturé le ${new Date(heritage.first_shiny.at).toLocaleDateString()}` : `Aucun chromatique pour l'instant (${heritage.total_shinies} au total).`}
                      testid="heritage-first-shiny"
                    />
                    <HeritageCard
                      icon="🌟"
                      title="Premier légendaire"
                      sprite={heritage.first_legendary ? shinySprite(heritage.first_legendary.pokemon_id, false) : null}
                      name={heritage.first_legendary?.name}
                      detail={heritage.first_legendary ? `Capturé le ${new Date(heritage.first_legendary.at).toLocaleDateString()}` : "Aucun légendaire (Maître requis)."}
                      testid="heritage-first-legendary"
                    />
                    <HeritageCard
                      icon="🔆"
                      title="Première évolution"
                      sprite={heritage.first_evolution?.to_id ? shinySprite(heritage.first_evolution.to_id, false) : null}
                      name={heritage.first_evolution?.to_name}
                      detail={heritage.first_evolution ? `Le ${new Date(heritage.first_evolution.at).toLocaleDateString()}` : "Aucune évolution pour l'instant."}
                      testid="heritage-first-evolution"
                    />
                    <HeritageCard
                      icon="👑"
                      title="Maître de la Ligue"
                      name={heritage.league_master_at ? "Champion·ne" : null}
                      detail={heritage.league_master_at ? `Couronné(e) le ${new Date(heritage.league_master_at).toLocaleDateString()}` : "Pas encore."}
                      testid="heritage-league-master"
                    />
                    <HeritageCard
                      icon="⬆️"
                      title="Pokémon le plus fort"
                      sprite={heritage.highest_level_pokemon ? shinySprite(heritage.highest_level_pokemon.pokemon_id, heritage.highest_level_pokemon.is_shiny) : null}
                      name={heritage.highest_level_pokemon?.name}
                      detail={heritage.highest_level_pokemon ? `Niveau ${heritage.highest_level_pokemon.level}${heritage.highest_level_pokemon.is_shiny ? " ✨" : ""}` : "—"}
                      testid="heritage-strongest"
                    />
                    <HeritageCard
                      icon="❤️"
                      title="Espèce favorite"
                      sprite={heritage.favorite_species ? shinySprite(heritage.favorite_species.pokemon_id, false) : null}
                      name={heritage.favorite_species?.name}
                      detail={heritage.favorite_species ? `Capturé ${heritage.favorite_species.count} fois` : "—"}
                      testid="heritage-favorite"
                    />
                  </div>

                  {/* Tally */}
                  <div className="mt-5 panel p-3 bg-[#FCF9F2]">
                    <div className="overline mb-2">Bilan global</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                      <Tally label="Captures" value={heritage.total_captures}/>
                      <Tally label="Shinies" value={heritage.total_shinies} accent={heritage.total_shinies > 0}/>
                      <Tally label="Légendaires" value={heritage.total_legendaries} accent={heritage.total_legendaries > 0}/>
                      <Tally label="Badges" value={`${heritage.total_badges}/8`}/>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "map" && (
            <div data-testid="detail-map">
              <RegionMap
                trainerId={trainerId}
                regions={regions}
                onTravelled={load}
              />
            </div>
          )}

          {tab === "region" && (
            <div data-testid="detail-region">
              <p className="text-[11px] text-[#4A4A4A] font-mono mb-3">
                Région actuelle du dresseur. En changeant de région, son équipe est rangée au PC et verrouillée sur la région d&apos;origine.
                <br/>
                <em>Si le dresseur a déjà voyagé dans la région cible, il y retrouve son ancienne équipe.</em>
              </p>
              <div className="space-y-2">
                {regions.map((r) => {
                  const isCurrent = (t.current_region || "kanto") === r.id;
                  return (
                    <div
                      key={r.id}
                      className={`flex items-center justify-between px-3 py-2 border ${
                        isCurrent ? "border-[#1A1A1A] bg-[#FCF9F2]" : r.unlocked ? "border-[#2E4F32]" : "border-[#7A7A7A] opacity-60"
                      }`}
                      data-testid={`region-row-${r.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {r.unlocked ? <Map size={12}/> : <Lock size={12} className="text-[#7A7A7A]"/>}
                        <span className="font-serif-display text-lg">{r.name}</span>
                        {isCurrent && <span className="stamp text-[8px]">ICI</span>}
                      </div>
                      <button
                        data-testid={`region-move-${r.id}`}
                        className="btn-ink text-[10px] py-1 px-2"
                        disabled={!r.unlocked || isCurrent || t.league_status !== "completed"}
                        title={t.league_status !== "completed" ? "Le dresseur doit avoir conquis sa Ligue actuelle" : ""}
                        onClick={() => openRegionSwitch(r)}
                      >
                        Y envoyer
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {pokeDetail && (
        <PokemonDetailModal
          poke={pokeDetail.poke}
          context={pokeDetail.context}
          onClose={() => setPokeDetail(null)}
          onRelease={pokeDetail.context === "pc" ? async () => {
            try {
              await api.delete(`/pc/${pokeDetail.poke.id}`);
              toast.success("Pokémon relâché dans la nature.");
              setPokeDetail(null);
              await load();
            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
          } : undefined}
        />
      )}
      {regionSwitch && (
        <RegionSwitchModal
          trainerName={t.name}
          fromRegion={t.current_region || "kanto"}
          switchState={regionSwitch}
          onPick={(pid) => setRegionSwitch(s => ({ ...s, chosenStarter: pid }))}
          onCancel={() => setRegionSwitch(null)}
          onConfirm={confirmRegionSwitch}
          busy={busy}
        />
      )}
      <ForceActionModal
        trainerId={trainerId}
        action={forceAction}
        open={!!forceAction}
        onClose={() => setForceAction(null)}
        onAfterSubmit={load}
      />
    </div>
  );
}

function RegionSwitchModal({ trainerName, fromRegion, switchState, onPick, onCancel, onConfirm, busy }) {
  const { region, preview, chosenStarter } = switchState;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
      data-testid="region-switch-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="panel bg-[#F4EFE6] w-full max-w-lg p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="overline">Changement de région</div>
            <h3 className="font-serif-display text-2xl leading-tight">
              {fromRegion.toUpperCase()} → {region.name}
            </h3>
          </div>
          <button className="btn-ink text-xs px-2 py-1" onClick={onCancel} data-testid="region-switch-close">×</button>
        </div>

        <p className="font-mono text-[11px] text-[#4A4A4A] mb-3 leading-snug">
          L&apos;équipe actuelle de <strong>{trainerName}</strong> partira au PC et sera verrouillée sur <strong>{fromRegion.toUpperCase()}</strong> jusqu&apos;à son retour.
        </p>

        {preview.needs_starter ? (
          <div>
            <div className="overline mb-2">Choisis un starter de {region.name}</div>
            <div className="grid grid-cols-3 gap-2">
              {(preview.starters || []).map((s) => (
                <button
                  key={s.pokemon_id}
                  data-testid={`region-switch-starter-${s.pokemon_id}`}
                  onClick={() => onPick(s.pokemon_id)}
                  className={`panel p-2 text-center hover:bg-[#FCF9F2] ${chosenStarter === s.pokemon_id ? "border-[#B87333] bg-[#FCF9F2] ring-2 ring-[#B87333]" : ""}`}
                >
                  <img
                    src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${s.pokemon_id}.png`}
                    alt={s.name}
                    className="pixelated w-16 h-16 mx-auto"
                  />
                  <div className="font-serif-display text-base leading-tight">{s.name}</div>
                  <div className="font-mono text-[9px] text-[#4A4A4A]">#{s.pokemon_id}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="panel bg-[#FCF9F2] p-3 text-[11px] font-mono leading-snug" data-testid="region-switch-restore">
            <div className="overline mb-1">✅ Retour possible</div>
            {preview.stored_team_count > 0 ? (
              <>Une équipe verrouillée sur {region.name} est en réserve : <strong>{preview.stored_team_count} Pokémon</strong> vont être remis dans l&apos;équipe et déverrouillés.</>
            ) : (
              <>Aucune équipe stockée, mais <strong>{preview.fallback_available}</strong> Pokémon du dresseur (originaires de {region.name}) sont disponibles. Le premier disponible sera assigné.</>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4 justify-end">
          <button className="btn-ink text-xs px-3 py-1" onClick={onCancel} disabled={busy} data-testid="region-switch-cancel">Annuler</button>
          <button
            className="btn-ink btn-leaf text-xs px-3 py-1"
            onClick={onConfirm}
            disabled={busy || (preview.needs_starter && !chosenStarter)}
            data-testid="region-switch-confirm"
          >
            {busy ? "…" : preview.needs_starter ? "Partir avec ce starter" : "Confirmer le voyage"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, accent, ...rest }) {
  return (
    <div className="panel p-2" {...rest}>
      <div className="overline text-[9px]">{label}</div>
      <div className={`font-serif-display text-2xl leading-none ${accent ? "text-[#B87333]" : ""}`}>{value}</div>
    </div>
  );
}

function Tally({ label, value, accent }) {
  return (
    <div className="panel p-2 text-center">
      <div className="overline text-[9px] mb-1">{label}</div>
      <div className={`font-serif-display text-2xl leading-none ${accent ? "text-[#B87333]" : ""}`}>{value}</div>
    </div>
  );
}

function HeritageCard({ icon, title, sprite, name, detail, testid }) {  const empty = !name;
  return (
    <div className="panel p-3 flex items-start gap-3" data-testid={testid}>
      <div className="text-2xl leading-none shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="overline mb-1">{title}</div>
        <div className="flex items-center gap-2">
          {sprite && (
            <img src={sprite} alt="" className="pixelated w-10 h-10 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          )}
          <div className="min-w-0">
            <div className={`font-serif-display text-lg leading-tight truncate ${empty ? "text-[#9A9A9A] italic" : ""}`}>
              {name || "—"}
            </div>
            <div className="font-mono text-[10px] text-[#4A4A4A] mt-0.5 leading-snug">{detail}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ItemRow({ item, side, max, onTransfer, onUse, busy, testid }) {
  const [qty, setQty] = useState(1);
  const isLab = side === "lab";
  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(max, q + 1));
  const submit = () => onTransfer(Math.min(qty, max));
  return (
    <div className="panel p-2 flex items-center gap-2 flex-wrap" data-testid={testid}>
      <img src={item.sprite} alt={item.name} className="pixelated w-8 h-8 shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="font-serif-display text-base leading-none truncate">{item.name}</div>
        <div className="font-mono text-[10px] text-[#4A4A4A]">x{item.qty} · {item.type}</div>
      </div>
      <div className="flex items-center gap-1 flex-wrap justify-end">
        {onUse && (
          <button
            data-testid={`${testid}-use`}
            className="btn-ink btn-leaf text-[10px] py-1 px-2"
            onClick={onUse}
            disabled={busy || max <= 0}
            title="Utiliser cet objet sur le dresseur"
          >
            ✦ Utiliser
          </button>
        )}
        <button data-testid={`${testid}-dec`} className="btn-ink text-[10px] px-1.5 py-0.5" onClick={dec} disabled={busy || qty <= 1}>−</button>
        <input
          data-testid={`${testid}-qty`}
          type="number"
          min="1"
          max={max}
          value={qty}
          onChange={(e) => setQty(Math.max(1, Math.min(max, parseInt(e.target.value || "1", 10))))}
          className="w-10 text-center font-mono text-xs border border-[#2B2B2B] bg-[#FCF9F2] px-1 py-0.5"
        />
        <button data-testid={`${testid}-inc`} className="btn-ink text-[10px] px-1.5 py-0.5" onClick={inc} disabled={busy || qty >= max}>+</button>
        <button
          data-testid={`${testid}-go`}
          className={`btn-ink ${isLab ? "btn-leaf" : "btn-accent"} text-[10px] py-1 px-2 ml-1`}
          onClick={submit}
          disabled={busy || max <= 0}
        >
          {isLab ? <><ArrowDown size={10}/> Donner</> : <><ArrowUp size={10}/> Récupérer</>}
        </button>
      </div>
    </div>
  );
}

function makeCountdown(iso) {
  if (!iso) return { label: "—", urgent: false };
  const ms = new Date(iso).getTime() - Date.now();
  const remaining = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return {
    label: remaining > 0 ? `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}` : "TERMINÉ",
    urgent: remaining > 0 && remaining <= 10,
  };
}
